<template>
  <v-card class="mb-4" style="height: 460px;">
    <v-card-title class="d-flex justify-space-between align-center">
      {{ $t('documentUploads') }}
      <v-btn-toggle
        v-model="selectedMetric"
        mandatory
        class="ml-4"
      >
        <v-btn value="count">{{ $t('count') }}</v-btn>
        <v-btn value="size">{{ $t('size') }}</v-btn>
      </v-btn-toggle>
    </v-card-title>
    <v-card-text>
      <v-skeleton-loader v-if="loading" type="image" height="400px" />
      <v-chart v-else class="chart" ref="chartRef" :option="chartOption" />
    </v-card-text>         
  </v-card>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart } from "echarts/charts";
import { TooltipComponent, TitleComponent, GridComponent, LegendComponent } from "echarts/components";
import VChart from "vue-echarts";

use([CanvasRenderer, LineChart, TooltipComponent, TitleComponent, GridComponent, LegendComponent]);

const store = useStore();
const { t } = useI18n();
const chartColor = computed(() => store.getters.chartFontColor);
const dataLoaded = computed(() => store.getters.getDataLoaded);

const chartRef = ref(null);
const chartOption = ref({});
const selectedMetric = ref('count');
const chartData = ref({
  dates: [],
  counts: [],
  sizes: []
});
const loading = ref(true);

const fetchDocumentData = async () => {
  try {
    loading.value = true;
    const conn = store.getters.getDBConnection;
    if (!conn) {
      console.error('DuckDB connection not available');
      return;
    }
    
    // Query to get daily document counts and sizes
    const query = `
      WITH DailyStats AS (
        SELECT 
          DATE_TRUNC('day', "CreatedDate") AS day,
          COUNT(*) AS daily_count,
          SUM("FileSize") AS daily_size
        FROM filteredDocuments
        WHERE "IsFolder" = FALSE
        GROUP BY DATE_TRUNC('day', "CreatedDate")
        ORDER BY day
      )
      SELECT 
        day,
        daily_count,
        daily_size,
        SUM(daily_count) OVER (ORDER BY day) AS cumulative_count,
        SUM(daily_size) OVER (ORDER BY day) AS cumulative_size
      FROM DailyStats
      ORDER BY day
    `;
    
    const result = await conn.query(query);
    const rows = result.toArray();
    
    const dates = [];
    const counts = [];
    const sizes = [];
    
    rows.forEach(row => {
      // Format the date as YYYY-MM-DD
      const date = new Date(row.day).toISOString().split('T')[0];
      dates.push(date);
      counts.push(Number(row.cumulative_count));
      sizes.push(Number(row.cumulative_size));
    });
    
    chartData.value = {
      dates,
      counts,
      sizes
    };
  } catch (error) {
    console.error('Error fetching document data for chart:', error);
    chartData.value = {
      dates: [],
      counts: [],
      sizes: []
    };
  } finally {
    loading.value = false;
  }
};

const formatSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  // Return with appropriate unit and 2 decimal places
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const updateChartOption = () => {
  const { dates, counts, sizes } = chartData.value;
  const isCount = selectedMetric.value === 'count';

  chartOption.value = {
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        const date = params[0].name;
        const value = params[0].value;
        const metricName = isCount ? t('totalCount') : 'Total Size';
        const formattedValue = isCount ? value : formatSize(value);
        return `Date: ${date}<br>${metricName}: ${formattedValue}`;
      }
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: dates,
      axisLabel: {
        color: chartColor.value,
        rotate: 45
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: chartColor.value,
        formatter: isCount ? '{value}' : function(value) {
          return formatSize(value);
        }
      }
    },
    series: [{
      data: isCount ? counts : sizes,
      type: 'line',
      name: isCount ? 'Cumulative Count' : 'Cumulative Size',
      smooth: true,
      itemStyle: {
        color: '#5470c6'
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [{
            offset: 0,
            color: 'rgba(84,112,198,0.5)'
          }, {
            offset: 1,
            color: 'rgba(84,112,198,0.1)'
          }]
        }
      }
    }],
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      containLabel: true
    }
  };
};

// Watch for data loaded state
watch(() => dataLoaded.value, async (newValue) => {
  if (newValue) {
    await fetchDocumentData();
    updateChartOption();
  }
});

watch(selectedMetric, () => {
  updateChartOption();
});

watch(() => chartColor.value, () => {
  updateChartOption();
});

onMounted(async () => {
  if (dataLoaded.value) {
    await fetchDocumentData();
    updateChartOption();
  }
  
  window.addEventListener('resize', () => {
    if (chartRef.value) {
      chartRef.value.resize();
    }
  });
});
</script>

<style scoped>
.chart {
  height: 400px;
}
</style>